import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VideoService } from '../video.service';
import { DurationPipe } from '../../pipe/duration.pipe';
import { ActivatedRoute } from '@angular/router';

interface Video {
  _id: string;
  title: string;
  description: string;
  url: string;
  thumbnail: string;
  module: string;
  date: string;
  views: number;
  duration: number;
  supportingFiles: string[];
  projectId: string;
  likesCount?: number;
  favoritesCount?: number;
  likedByCurrentUser?: boolean;
  favoritedByCurrentUser?: boolean;
  comments?: any[];
}

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [CommonModule, FormsModule, DurationPipe],
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements OnInit, OnChanges {
  @Input() selectedVideo!: Video;
  @Input() upNextVideos: Video[] = [];
  @Output() playVideoChange = new EventEmitter<Video>();

  likesCount = 0;
  favoritesCount = 0;
  liked = false;
  favorited = false;
  comments: any[] = [];
  newComment = '';
  viewsCount: number = 0;

  user: any;
  users: any[] = [];
  isDescOpen: boolean = false;
  lastViewedVideoId: string | null = null;

  constructor(private videoService: VideoService, private route: ActivatedRoute) {}

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem('userData') || '{}');
    // ✅ Initialize with parent-passed data (no API call here)
    // if (this.selectedVideo) {
    //   this.likesCount = this.selectedVideo.likesCount || 0;
    //   this.favoritesCount = this.selectedVideo.favoritesCount || 0;
    //   this.liked = this.selectedVideo.likedByCurrentUser || false;
    //   this.favorited = this.selectedVideo.favoritedByCurrentUser || false;
    //   this.comments = this.selectedVideo.comments || [];
    // }
    this.route.paramMap.subscribe(params => {
      const videoId = params.get('id');
      if (videoId && videoId !== this.lastViewedVideoId) {
        this.addView(videoId);
      }
    });

    console.log('liked:', this.likesCount, 'favorited:', this.favoritesCount, 'comments:', this.comments);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedVideo'] && this.selectedVideo) {
      this.likesCount = this.selectedVideo.likesCount || 0;
      this.favoritesCount = this.selectedVideo.favoritesCount || 0;
      this.liked = this.selectedVideo.likedByCurrentUser || false;
      this.favorited = this.selectedVideo.favoritedByCurrentUser || false;
      this.comments = this.selectedVideo.comments || [];
      this.viewsCount = this.selectedVideo.views ?? 0;

      if (this.selectedVideo._id && this.selectedVideo._id !== this.lastViewedVideoId) {
        this.addView(this.selectedVideo._id);
      }
  }
    console.log('liked:', this.likesCount, 'favorited:', this.favoritesCount, 'comments:', this.comments);
  }

  private addView(videoId: string) {
    if (!this.user?.user_id) return;
    this.lastViewedVideoId = videoId;

    this.videoService.addView(videoId, this.user.user_id).subscribe({
      next: () => {
        this.viewsCount++;
        if (this.selectedVideo && this.selectedVideo._id === videoId) {
          this.selectedVideo.views = this.viewsCount;
        }
      },
      error: (err) => console.error('Failed to record view', err),
    });
  }

  toggleLike() {
    this.videoService.toggleLike(this.selectedVideo._id, this.user.user_id).subscribe({
      next: (res) => {
        this.liked = res.liked;
        this.likesCount = res.likesCount;

        this.selectedVideo.likedByCurrentUser = res.liked;
        this.selectedVideo.likesCount = res.likesCount;

        const idx = this.upNextVideos.findIndex(v => v._id === this.selectedVideo._id);
        if (idx !== -1) {
          this.upNextVideos[idx].likedByCurrentUser = res.liked;
          this.upNextVideos[idx].likesCount = res.likesCount;
        }
      },
      error: (err) => console.error('Error toggling like', err),
    });
  }

  toggleFavorite() {
    // Call API
    this.videoService.toggleFavorite(this.selectedVideo._id, this.user.user_id).subscribe({
      next: (res) => {
        // ✅ Update both component state and bound object
        this.favorited = res.favorited;
        this.favoritesCount = res.favoritesCount;

        this.selectedVideo.favoritedByCurrentUser = res.favorited;
        this.selectedVideo.favoritesCount = res.favoritesCount;

        // Optional: if video also appears in the up-next list, sync that one too
        const idx = this.upNextVideos.findIndex(v => v._id === this.selectedVideo._id);
        if (idx !== -1) {
          this.upNextVideos[idx].favoritedByCurrentUser = res.favorited;
          this.upNextVideos[idx].favoritesCount = res.favoritesCount;
        }

        console.log('Updated Favorite:', res);
      },
      error: (err) => console.error('Error toggling favorite', err),
    });
  }

  postComment() {
    if (!this.newComment.trim()) return;

    this.videoService.addComment(this.selectedVideo._id, this.user.user_id, this.newComment).subscribe({
      next: (comment) => {
        const formattedComment = {
          _id: comment._id,
          text: comment.text,
          createdAt: comment.createdAt,
          userName: `${comment.user?.first_name || ''} ${comment.user?.last_name || ''}`.trim(),
          userEmail: comment.user?.primary_email_id || '',
          profile_image: comment.user?.profile_image || 'assets/default-avatar.png',
        };

        // ✅ Only add to this.comments (UI is bound to this)
        this.comments.unshift(formattedComment);

        // ✅ Sync selectedVideo.comments only if it’s a different array
        if (this.selectedVideo.comments !== this.comments) {
          if (!this.selectedVideo.comments) this.selectedVideo.comments = [];
          this.selectedVideo.comments.unshift(formattedComment);
        }

        this.newComment = '';
      },
      error: (err) => console.error('Error adding comment', err),
    });
  }

  playVideo(video: Video) {
    this.playVideoChange.emit(video);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  extractFileName(fileUrl: string): string {
    try {
      return decodeURIComponent(fileUrl.split('/').pop() || 'Document');
    } catch {
      return 'Document';
    }
  }

  getFileIcon(fileUrl: string): string {
    const ext = fileUrl.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc':
      case 'docx': return 'description';
      case 'xls':
      case 'xlsx': return 'table_chart';
      case 'ppt':
      case 'pptx': return 'slideshow';
      case 'txt': return 'article';
      default: return 'attach_file';
    }
  }
}
